

/**
 * 数字格式化 - 根据环境变量自动选择格式
 * @param value 要格式化的数值
 * @param options 格式化选项
 * @returns 格式化后的数字字符串
 */
export const numberFormat = (
  value: number | string,
  options: {
    length?: number       // 小数位数（简化参数）
    decimals?: number
    maxDecimals?: number
    minDecimals?: number
    forceStandardGrouping?: boolean  // 新增：强制使用标准3位分组
  } = {}
): string => {
  // 从环境变量获取默认格式，并映射到标准的 locale 格式
  const envFormat = import.meta.env.VITE_CURRENCY_FORMAT || 'US'
  
  // 映射简化的地区代码到标准的 locale 格式
  const localeMap: Record<string, string> = {
    'US': 'en-US',
    'IN': 'en-IN', 
    'CN': 'en-US',
    'JP': 'ja-JP',
    'DE': 'de-DE',
    'FR': 'fr-FR',
    'GB': 'en-GB',
    'RU': 'ru-RU',
    'BR': 'pt-BR',
    'KR': 'ko-KR'
  }
  
  const defaultLocale = localeMap[envFormat] || 'en-US'  // 改为默认巴西格式
  
  const {
    length,                  // 新增：length 参数
    decimals = 2,
    maxDecimals = decimals,
    minDecimals = 0,
    forceStandardGrouping = false  // 新增：强制使用标准3位分组
  } = options

  // 如果提供了 length 参数，则使用它来设置小数位数
  const finalDecimals = length !== undefined ? length : decimals
  const finalMaxDecimals = length !== undefined ? length : maxDecimals
  const finalMinDecimals = length !== undefined ? length : minDecimals

  const num = Number(value)
  if (isNaN(num)) {
    return '0'
  }

  // 处理小数位数：超出部分直接舍弃，不足时保持实际位数
  const processedNum = Number(num.toFixed(finalMaxDecimals))
  
  // 计算实际的小数位数
  const actualDecimals = (processedNum.toString().split('.')[1] || '').length
  const minFractionDigits = Math.max(finalMinDecimals, Math.min(actualDecimals, finalDecimals))
  const maxFractionDigits = Math.min(finalMaxDecimals, finalDecimals)

  try {
    // 如果强制使用标准3位分组，则使用 en-US locale
    const localeToUse = forceStandardGrouping ? 'en-US' : defaultLocale
    
    // 使用标准的 Intl.NumberFormat 来处理不同地区的格式化
    const formatter = new Intl.NumberFormat(localeToUse, {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      useGrouping: true
    })
    
    // 移除硬编码的 '.00'，让 Intl.NumberFormat 自动处理小数位
    const result = formatter.format(processedNum)
    
    return result
  } catch (error) {
    // 如果格式化失败，回退到基础格式化
    console.warn('Number formatting failed, using fallback:', error)
    return Number(processedNum.toFixed(minFractionDigits)).toString()
  }
}

